import { BlobOptions } from "buffer";

export interface CollectionFileData {
  name: string;
  description: string;
  social_links: string[];
  imageUrl: string;
  backgroundUrl: string;
}

export interface CollectionDataCache {
  owner: string;
  address: string;
  mint_limit: number;
  price: number;
  next_item_index?: number;
  next_item_address?: string;
  data_url: string;
  item_data_url?: string;
  is_sbt?: boolean;
  mint_time_limit?: number;
  is_hidden_by_owner?: boolean;
  enable_whitelist?: boolean | null;
  user_item_limit?: number;
  stars?: number;
  volume?: number;
  experience?: number;
}

export interface OwnerData {
  collections: OwnerCollectionData[];
  items: OwnerNftData[];
}

export interface OwnerCollectionData {
  address: string;
  data_url: string;
  id: Number;
  item_data_url: string;
  mint_limit: number;
  next_item_index: number;
  owner_id: number;
  price: number;
  saved_at: string;
  is_hidden_by_owner?: boolean;
}
export interface OwnerNftData {
  address: string;
  collection: string;
  data_url: string;
  index_in_collection: string;
  minter: string;
}

export interface Attribute {
  trait_type: string;
  value: string;
}

export interface ItemFileData {
  name?: string;
  description?: string;
  marketplace?: string;
  image: string;
  attributes: Attribute[];
  content_type?: string;
  content_url?: string;
}

export interface CollectionMintEntry {
  collection: CollectionFileData;
  item: ItemFileData;
  collectionAddress?: string;
}

export interface CollectionDataUrls {
  collectionFileUrl: string;
  itemFileUrl: string;
}

export interface CollectionStandardData {
  name: string;
  description: string;
  image: string;
  cover_image: string;
  social_links: string[];
}

export interface CollectionTotalData {
  standardData: CollectionStandardData;
  nextItemIndex: number;
  nextItemAddress: string;
}

export interface CollectionMasterStandardData {
  mint_limit: number;
  price: number;
  index_in_collection: number;
  master: string;
  enable_whitelist?: string;
  user_item_limit?: number;
}

export interface MasterStandardData {
  master: string;
  collection_creation_price: number;
  next_collection_index: number;
}

export interface LibItem {
  id: number;
  name: string;
}

export interface SkinData {
  address: string;
  category_id: number;
  collection_data: CollectionDataCache;
  item?: {
    address: string;
  };
  items?: {
    address: string;
    index: number;
    metadata?: {
      key: string;
      value: string;
    }[];
  }[];
}

export interface CollectionStats {
  name: string;
  description: string;
  background: string;
  image: string;
  nft_image: string;
  nft_image_type?: string;
  nft_image_content?: string;
  minted_nfts: number;
  contract_address: string;
  owner_address: string;
  nextItemAddress: string;
  total: number;
  price: number;
  earned: number;
  available: number;
  mint_limit: number;
  mint_time_limit: number;
  is_sbt: boolean;
  enable_whitelist: boolean;
  user_item_limit: number;
}

export interface CollectionMediaLoadingState {
  background: boolean;
  image: boolean;
  nftImage: boolean;
  loaded: boolean;
}

export interface ItemInCollection {
  address: string;
  index: number;
}

export interface NftCard {
  id: string;
  image: string;
  name: string;
  mintedAddress: string;
}

export async function fetchData(dataUrl: string): Promise<any | null> {
  try {
    const response = await fetch(dataUrl);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching data:", error);
    return null;
  }
}

export async function fetchFile(
  fileUrl: string,
  fileName?: string
): Promise<File | null> {
  try {
    const response = await fetch(fileUrl);
    if (!response.ok) {
      throw new Error(`Failed to fetch file: ${response.statusText}`);
    }

    const blob = await response.blob();
    const name = fileName ?? fileUrl.split("/").pop() ?? "downloaded-file";
    return new File([blob], name, { type: blob.type });
  } catch (error) {
    console.error("Error fetching file:", error);
    return null;
  }
}
